import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    add,
    cloneRecord,
    deleteRecord,
    update
} from '../services/ProductsService';
import {createProductQueryOptions} from '../query-options/createProductQueryOptions';
import {Product} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const productQuery = useQuery({
                                      ...createProductQueryOptions(productId!),
                                      enabled: !!productId
                                  });

    // CREATE MUTATION
    const createMutation = useMutation({
                                           mutationFn: (values: Product) => {
                                               const {
                                                   lineItems: _lineItems,
                                                   created: _created,
                                                   createdByUsername: _createdByUsername,
                                                   createdByUser: _createdByUser,
                                                   updated: _updated,
                                                   ...rest
                                               } = values;
                                               return add(rest);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Product created');
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                           },
                                           onError: (error) => {
                                               notifyError(error);
                                           }
                                       });

    // UPDATE MUTATION
    const updateMutation = useMutation({
                                           mutationFn: (values: Product) => {
                                               if (!productId) throw new Error('productId is required parameter');
                                               const {
                                                   lineItems: _lineItems,
                                                   created: _created,
                                                   createdByUsername: _createdByUsername,
                                                   createdByUser: _createdByUser,
                                                   updated: _updated,
                                                   ...rest
                                               } = values;
                                               return update(productId, rest);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Product updated');
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                               queryClient.invalidateQueries({queryKey: ['product', productId]});
                                           },
                                           onError: (error) => {
                                               notifyError(error);
                                           }
                                       });

    // CLONE MUTATION
    const cloneMutation = useMutation({
                                          mutationFn: () => {
                                              if (!productId) throw new Error('productId is required parameter');
                                              return cloneRecord(productId);
                                          },
                                          onSuccess: () => {
                                              notifySuccess('Product cloned');
                                              queryClient.invalidateQueries({queryKey: ['products']});
                                              queryClient.invalidateQueries({queryKey: ['all-products']});
                                          },
                                          onError: (error) => {
                                              notifyError(error);
                                          }
                                      });

    // DELETE MUTATION
    const deleteMutation = useMutation({
                                           mutationFn: () => {
                                               if (!productId) throw new Error('productId is required parameter');
                                               return deleteRecord(productId);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Product deleted');
                                               queryClient.invalidateQueries({queryKey: ['products']});
                                               queryClient.invalidateQueries({queryKey: ['all-products']});
                                           },
                                           onError: (error) => {
                                               notifyError(error);
                                           }
                                       });

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        fetchRecord: productQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        cloneRecord: cloneMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        cloneMutation,
        deleteMutation
    };
};
